import React, { useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminLayout from './AdminLayout';
import supabase from '../../supabase/supabase';

const { FiPlus, FiTrash2, FiEdit2, FiSave, FiX, FiTag, FiRefreshCw } = FiIcons;
const emptyForm = { name: '', code: '', discount_percent: '10' };

function AdminPromoCodes() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', code: '', discount_percent: '' });
  const [form, setForm] = useState(emptyForm);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = async () => {
    setLoading(true);
    const { data } = await supabase
      .from('admin_promo_codes_1750000012')
      .select('*')
      .order('created_at', { ascending: false });
    setCodes(data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const addCode = async () => {
    if (!form.name || !form.code || !form.discount_percent) return;
    await supabase.from('admin_promo_codes_1750000012').insert({
      name: form.name,
      code: form.code.trim().toUpperCase(),
      discount_percent: Number(form.discount_percent),
      active: true
    });
    setForm(emptyForm);
    setShowAdd(false);
    load();
  };

  const startEdit = (code) => {
    setEditingId(code.id);
    setEditForm({ name: code.name, code: code.code, discount_percent: String(code.discount_percent) });
  };

  const saveEdit = async (id) => {
    await supabase.from('admin_promo_codes_1750000012').update({
      name: editForm.name,
      code: editForm.code.trim().toUpperCase(),
      discount_percent: Number(editForm.discount_percent),
      updated_at: new Date().toISOString()
    }).eq('id', id);
    setEditingId(null);
    load();
  };

  const toggleActive = async (code) => {
    await supabase.from('admin_promo_codes_1750000012')
      .update({ active: !code.active, updated_at: new Date().toISOString() })
      .eq('id', code.id);
    load();
  };

  const deleteCode = async (id) => {
    if (!confirm('Delete this promo code?')) return;
    await supabase.from('admin_promo_codes_1750000012').delete().eq('id', id);
    setCodes((prev) => prev.filter((c) => c.id !== id));
    setSelectedIds((prev) => { const n = new Set(prev); n.delete(id); return n; });
  };

  const batchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Delete ${selectedIds.size} promo code(s)?`)) return;
    const ids = [...selectedIds];
    await supabase.from('admin_promo_codes_1750000012').delete().in('id', ids);
    setSelectedIds(new Set());
    load();
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === codes.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(codes.map((c) => c.id)));
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div>
          <h1>Promo Codes</h1>
          <p>Create store-wide discount codes customers can use at checkout</p>
        </div>
        <div className="admin-header-actions">
          <button className="admin-btn secondary" onClick={load}>
            <SafeIcon icon={FiRefreshCw} /> Refresh
          </button>
          <button className="admin-btn primary" onClick={() => setShowAdd(true)}>
            <SafeIcon icon={FiPlus} /> Add Promo Code
          </button>
        </div>
      </div>

      {selectedIds.size > 0 && (
        <div className="batch-action-bar" style={{ margin: '0 28px 16px' }}>
          <span>{selectedIds.size} selected</span>
          <button className="admin-btn secondary batch-delete-btn" onClick={batchDelete}>
            <SafeIcon icon={FiTrash2} /> Delete Selected
          </button>
          <button className="admin-btn secondary" onClick={() => setSelectedIds(new Set())}>Clear</button>
        </div>
      )}

      <div className="admin-card">
        {loading ? (
          <div className="admin-loading">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="admin-empty">
            <SafeIcon icon={FiTag} />
            <p>No promo codes yet</p>
            <span className="small-text">Create your first store discount code</span>
          </div>
        ) : (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>
                    <input type="checkbox" checked={codes.length > 0 && selectedIds.size === codes.length} onChange={toggleSelectAll} />
                  </th>
                  <th>Name</th>
                  <th>Code</th>
                  <th>Discount</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {codes.map((code) => (
                  <tr key={code.id} className={selectedIds.has(code.id) ? 'row-selected' : ''}>
                    <td>
                      <input type="checkbox" checked={selectedIds.has(code.id)} onChange={() => toggleSelect(code.id)} />
                    </td>
                    <td>
                      {editingId === code.id ? (
                        <input
                          value={editForm.name}
                          onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                          className="admin-inline-input"
                        />
                      ) : (
                        <strong>{code.name}</strong>
                      )}
                    </td>
                    <td>
                      {editingId === code.id ? (
                        <input
                          value={editForm.code}
                          onChange={(e) => setEditForm((f) => ({ ...f, code: e.target.value }))}
                          className="admin-inline-input mono"
                          style={{ textTransform: 'uppercase' }}
                        />
                      ) : (
                        <span className="mono" style={{ fontWeight: 600, color: '#0e8f5b' }}>{code.code}</span>
                      )}
                    </td>
                    <td>
                      {editingId === code.id ? (
                        <input
                          type="number"
                          value={editForm.discount_percent}
                          onChange={(e) => setEditForm((f) => ({ ...f, discount_percent: e.target.value }))}
                          className="admin-inline-input"
                          style={{ width: 70 }}
                        />
                      ) : (
                        <span>{code.discount_percent}%</span>
                      )}
                    </td>
                    <td>
                      <button
                        className={`admin-action-btn ${code.active ? 'primary' : 'danger'}`}
                        onClick={() => toggleActive(code)}
                      >
                        {code.active ? 'Active' : 'Disabled'}
                      </button>
                    </td>
                    <td className="small-text">{new Date(code.created_at).toLocaleDateString()}</td>
                    <td>
                      <div className="admin-row-actions">
                        {editingId === code.id ? (
                          <>
                            <button className="admin-icon-btn primary" onClick={() => saveEdit(code.id)} title="Save">
                              <SafeIcon icon={FiSave} />
                            </button>
                            <button className="admin-icon-btn" onClick={() => setEditingId(null)} title="Cancel">
                              <SafeIcon icon={FiX} />
                            </button>
                          </>
                        ) : (
                          <>
                            <button className="admin-icon-btn" onClick={() => startEdit(code)} title="Edit">
                              <SafeIcon icon={FiEdit2} />
                            </button>
                            <button className="admin-icon-btn danger" onClick={() => deleteCode(code.id)} title="Delete">
                              <SafeIcon icon={FiTrash2} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAdd && (
        <div className="admin-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="admin-modal admin-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2>Add Promo Code</h2>
              <button className="admin-icon-btn" onClick={() => setShowAdd(false)}>
                <SafeIcon icon={FiX} />
              </button>
            </div>
            <div className="admin-modal-body">
              <div className="editor-field">
                <label>Promo Name</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Summer Sale"
                />
              </div>
              <div className="editor-field">
                <label>Promo Code</label>
                <input
                  value={form.code}
                  onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="e.g. SUMMER20"
                  style={{ textTransform: 'uppercase', fontFamily: 'monospace' }}
                />
              </div>
              <div className="editor-field">
                <label>Discount Percentage (%)</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  value={form.discount_percent}
                  onChange={(e) => setForm((f) => ({ ...f, discount_percent: e.target.value }))}
                  placeholder="e.g. 10"
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn secondary" onClick={() => setShowAdd(false)}>Cancel</button>
              <button className="admin-btn primary" onClick={addCode}>
                <SafeIcon icon={FiSave} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminPromoCodes;